/**
 * useUserRole — claims-backed role hook (RW-11).
 *
 * Subscribes to the current user's `users/{uid}` Firestore doc and exposes
 * the `role` field plus a set of derived permission booleans. The Firestore
 * mirror is the fast UI source of truth — server-side enforcement uses the
 * matching custom claim (`auth.token.role`) set by `setUserRole` /
 * `onUserCreated` Cloud Functions.
 *
 * Permanent emergency fallback: clint@reverealtors.com is always treated as
 * super_admin even if the doc/claim hasn't been set yet. This is the LAST
 * check, not the primary one — the role field wins when present.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { db, auth } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  CANONICAL_ROLES,
  ACCESS_LEVELS,
  PERMANENT_SUPER_ADMIN_EMAIL,
  normalizeRoles,
  accessLevelForRoles,
} from '../lib/roles';

// Role vocabulary is owned by src/lib/roles.js (RW-698 added broker +
// office_coordinator). All role validation flows through normalizeRoles.

// deriveFlags accepts a single role string OR a roles[] array — normalizeRoles
// handles both, so callers during the single→multi transition keep working.
function deriveFlags(rolesInput) {
  const roles = normalizeRoles(rolesInput);
  const accessLevel = accessLevelForRoles(roles);
  const isSuperAdmin = accessLevel === ACCESS_LEVELS.SUPER_ADMIN;
  const isAdmin = accessLevel === ACCESS_LEVELS.ADMIN || isSuperAdmin;
  const isAgentServices = roles.includes('agent_services');
  const isTrainer = roles.includes('trainer');
  const isTC = roles.includes('tc');
  const isBroker = roles.includes('broker');
  const isOfficeCoordinator = roles.includes('office_coordinator');
  const isAgent = roles.includes('agent');
  // Highest-privilege role, for legacy consumers that still read a single `role`.
  const primaryRole = CANONICAL_ROLES.find((r) => roles.includes(r)) || 'agent';
  return {
    role: primaryRole,
    roles,
    accessLevel,
    isSuperAdmin,
    isAdmin,
    isAgentServices,
    isTrainer,
    isTC,
    isBroker,
    isOfficeCoordinator,
    isAgent,
    // Permission helpers — preserved exactly from the single-role era; nothing
    // is downgraded. agent_services/tc keep their prior effective powers.
    canApproveChecklists: isAdmin,
    canDeletePermanently: isSuperAdmin,
    canAccessDevTools: isSuperAdmin,
    canAccessAdminPanel: isAdmin,
    canAccessTraining: isAdmin || isTrainer,
    canCreateOnBehalf: isAdmin || isTC,
    canWriteAll: isAdmin || isTC,
  };
}

// Context default before the user doc loads. Derived from ['agent'] so it stays
// in sync with deriveFlags' shape, plus loaded:false.
const DEFAULT_VALUE = { ...deriveFlags(['agent']), loaded: false };

const UserRoleContext = createContext(DEFAULT_VALUE);

export function UserRoleProvider({ user, children }) {
  const [roles, setRoles] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setRoles([]);
      setLoaded(false);
      return undefined;
    }
    const ref = doc(db, 'users', user.uid);
    return onSnapshot(
      ref,
      (snap) => {
        const data = snap.data();
        // roles[] is the source of truth; fall back to the legacy single `role`
        // string until the migration backfills every doc.
        const raw = Array.isArray(data?.roles) ? data.roles : data?.role ? [data.role] : [];
        let resolved = normalizeRoles(raw);
        // Permanent emergency fallback — never lock Clint out
        if (!resolved.length && (user.email || '').toLowerCase() === PERMANENT_SUPER_ADMIN_EMAIL) {
          resolved = ['super_admin'];
        }
        setRoles(resolved.length ? resolved : ['agent']);
        setLoaded(true);
      },
      () => {
        // Read failed (rules, network) — fall back to safe default
        const fallback =
          (user.email || '').toLowerCase() === PERMANENT_SUPER_ADMIN_EMAIL ? ['super_admin'] : ['agent'];
        setRoles(fallback);
        setLoaded(true);
      },
    );
  }, [user?.uid, user?.email]);

  // Stable dependency key for the array so the token-refresh effect doesn't
  // re-fire on every render (new array identity) when roles haven't changed.
  const roleKey = roles.join(',');

  // Force a token refresh when roles change so the server sees the new claim
  useEffect(() => {
    if (!loaded || !auth.currentUser) return;
    auth.currentUser.getIdToken(true).catch(() => {});
  }, [roleKey, loaded]);

  const value = useMemo(() => {
    return { ...deriveFlags(roles.length ? roles : ['agent']), loaded };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleKey, loaded]);

  return <UserRoleContext.Provider value={value}>{children}</UserRoleContext.Provider>;
}

export function useUserRole() {
  return useContext(UserRoleContext);
}

/**
 * Stand-alone helper for non-component code (e.g. permission checks inside
 * Firestore handlers or older callbacks). Accepts a user object that may
 * include `roles` (preferred), a legacy single `role`, or just an email (for
 * the fallback). Returns the highest-privilege single role for legacy callers.
 */
export function resolveRole(userOrToken) {
  if (!userOrToken) return 'agent';
  const roles = normalizeRoles(
    Array.isArray(userOrToken.roles) ? userOrToken.roles : userOrToken.role,
  );
  if (roles.length) return CANONICAL_ROLES.find((r) => roles.includes(r)) || 'agent';
  const email = String(userOrToken.email || '').toLowerCase();
  if (email === PERMANENT_SUPER_ADMIN_EMAIL) return 'super_admin';
  return 'agent';
}

// Accepts a single role string or a roles[] array (deriveFlags normalizes).
export function deriveRoleFlags(rolesInput) {
  return deriveFlags(rolesInput || 'agent');
}

export const PERMANENT_SUPER_ADMIN = PERMANENT_SUPER_ADMIN_EMAIL;
